import { deltaTone, formatDelta, formatNumber } from "./util";

interface Stage {
  stage?: { name?: string; rationale?: string };
  cumulative_part_ids?: string[];
  simulation?: {
    top_score_deltas?: { dimension?: string; delta_abs?: number }[];
    scene_lens?: Record<
      string,
      { after_match_pct?: number; delta_match_pct?: number }
    >;
    dynamics?: {
      modified?: { estimated_lat_g?: number };
      deltas?: { estimated_lat_g?: number };
    };
  };
}

interface Validation {
  violations?: { severity?: string; rule_id?: string; message?: string }[];
}

interface EvaluatePayload {
  stages?: Stage[];
  validation?: Validation;
}

const SCENE_LABELS: Record<string, string> = {
  nordschleife: "纽北",
  suzuka: "铃鹿",
  mountain_road: "山路",
  city_dense: "拥堵",
};

export function StagesPanel({ payload }: { payload: unknown }) {
  const p = (payload ?? {}) as EvaluatePayload;
  const stages = p.stages ?? [];
  const blocking = (p.validation?.violations ?? []).filter(
    (v) => v.severity === "block",
  );

  if (stages.length === 0) {
    return <p className="muted small">没有改装阶段。</p>;
  }

  return (
    <div className="stages-block">
      {blocking.length > 0 ? (
        <div className="stage-violation">
          <strong>阻断项 {blocking.length} 条：</strong>
          <ul>
            {blocking.map((v, i) => (
              <li key={`${v.rule_id ?? "v"}-${i}`}>
                <code>{v.rule_id ?? "rule"}</code> · {v.message ?? ""}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <ol className="stages-timeline">
        {stages.map((s, idx) => {
          const sim = s.simulation;
          const tone = deltaTone(sim?.dynamics?.deltas?.estimated_lat_g);
          const topDeltas = sim?.top_score_deltas?.slice(0, 3) ?? [];
          return (
            <li key={`${s.stage?.name ?? "stage"}-${idx}`} className={`stage-item tone-${tone}`}>
              <header>
                <span className="stage-index">阶段 {idx + 1}</span>
                <h4>{s.stage?.name ?? `阶段 ${idx + 1}`}</h4>
              </header>
              {s.stage?.rationale ? (
                <p className="muted small">{s.stage.rationale}</p>
              ) : null}
              <p className="stage-parts">
                累计部件 {s.cumulative_part_ids?.length ?? 0} 项
                {sim?.dynamics?.modified?.estimated_lat_g !== undefined ? (
                  <span className="stage-latg">
                    {" "}· lat g {formatNumber(sim.dynamics.modified.estimated_lat_g, 2)}
                    {sim.dynamics.deltas?.estimated_lat_g !== undefined ? (
                      <span className={`tone-${tone}`}>
                        {" "}({formatDelta(sim.dynamics.deltas.estimated_lat_g, 2)})
                      </span>
                    ) : null}
                  </span>
                ) : null}
              </p>
              {topDeltas.length > 0 ? (
                <ul className="stage-deltas">
                  {topDeltas.map((d, i) => (
                    <li key={`${d.dimension ?? "d"}-${i}`}>
                      {d.dimension}: {formatDelta(d.delta_abs, 1)}
                    </li>
                  ))}
                </ul>
              ) : null}
              {sim?.scene_lens ? (
                <ul className="stage-scenes">
                  {Object.entries(sim.scene_lens)
                    .filter(([sid]) => SCENE_LABELS[sid])
                    .map(([sid, v]) => (
                      <li key={sid}>
                        {SCENE_LABELS[sid]}: {formatNumber(v.after_match_pct, 1)}%
                        {typeof v.delta_match_pct === "number" ? (
                          <span className={`tone-${deltaTone(v.delta_match_pct)}`}>
                            {" "}({formatDelta(v.delta_match_pct, 1)})
                          </span>
                        ) : null}
                      </li>
                    ))}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
